import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../brand/BrandLogo';
import FeatureLockModal from '../entitlements/FeatureLockModal';
import { useAuth } from '../../hooks/useAuth';

type NavGroupId = 'main' | 'operations' | 'governance' | 'partner' | 'admin';
type SidebarIconName =
  | 'dashboard'
  | 'sites'
  | 'alerts'
  | 'reports'
  | 'queue'
  | 'issues'
  | 'evidence'
  | 'auth'
  | 'schedule'
  | 'policies'
  | 'audit'
  | 'team'
  | 'partners'
  | 'workspace'
  | 'billing';

interface AppSidebarProps {
  collapsed: boolean;
  unreadAlerts: number;
  alertsEnabled: boolean | null;
}

const ALERTS_UPGRADE_PATH = '/billing?plan=starter&source=alerts-lock';

interface SidebarItem {
  id: string;
  label: string;
  icon: SidebarIconName;
  to?: string;
  view?: string;
  section?: string;
  disabled?: boolean;
  locked?: boolean;
  badge?: string | number;
}

function SidebarIcon({ name }: { name: SidebarIconName }) {
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  const glyphs: Record<SidebarIconName, ReactNode> = {
    dashboard: (
      <>
        <rect x="4" y="4" width="7" height="7" rx="2" {...stroke} />
        <rect x="13" y="4" width="7" height="5" rx="2" {...stroke} />
        <rect x="4" y="13" width="7" height="7" rx="2" {...stroke} />
        <rect x="13" y="11" width="7" height="9" rx="2" {...stroke} />
      </>
    ),
    sites: (
      <>
        <circle cx="12" cy="12" r="8" {...stroke} />
        <path d="M4 12h16" {...stroke} />
        <path d="M12 4a12 12 0 0 1 0 16" {...stroke} />
        <path d="M12 4a12 12 0 0 0 0 16" {...stroke} />
      </>
    ),
    alerts: (
      <>
        <path d="M9 18h6" {...stroke} />
        <path d="M6 16h12l-1.2-1.7A4.4 4.4 0 0 1 16 11.8V10a4 4 0 1 0-8 0v1.8c0 .9-.28 1.77-.8 2.5L6 16Z" {...stroke} />
      </>
    ),
    reports: (
      <>
        <path d="M8 4h7l5 5v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" {...stroke} />
        <path d="M15 4v5h5" {...stroke} />
        <path d="M9 13h6" {...stroke} />
        <path d="M9 17h6" {...stroke} />
      </>
    ),
    queue: (
      <>
        <rect x="5" y="5" width="14" height="4" rx="2" {...stroke} />
        <rect x="5" y="10" width="14" height="4" rx="2" {...stroke} />
        <rect x="5" y="15" width="14" height="4" rx="2" {...stroke} />
      </>
    ),
    issues: (
      <>
        <path d="M12 4 4 8v4c0 4.2 2.5 7.3 8 8 5.5-.7 8-3.8 8-8V8l-8-4Z" {...stroke} />
        <path d="M12 9v4" {...stroke} />
        <path d="M12 16h.01" {...stroke} />
      </>
    ),
    evidence: (
      <>
        <path d="M8 4h8l4 4v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" {...stroke} />
        <path d="M16 4v4h4" {...stroke} />
        <path d="M9 12h6" {...stroke} />
        <path d="M9 16h4" {...stroke} />
      </>
    ),
    auth: (
      <>
        <path d="M12 4 5 7v5c0 4 2.2 6.9 7 8 4.8-1.1 7-4 7-8V7l-7-3Z" {...stroke} />
        <path d="M10 12.5 11.8 14 15 10.5" {...stroke} />
      </>
    ),
    schedule: (
      <>
        <circle cx="12" cy="13" r="7" {...stroke} />
        <path d="M12 9v4l2.5 2" {...stroke} />
        <path d="M8 3v3" {...stroke} />
        <path d="M16 3v3" {...stroke} />
      </>
    ),
    policies: (
      <>
        <path d="M7 5h10a2 2 0 0 1 2 2v12H9a3 3 0 0 0-3 3V7a2 2 0 0 1 1-2Z" {...stroke} />
        <path d="M9 9h7" {...stroke} />
        <path d="M9 13h7" {...stroke} />
      </>
    ),
    audit: (
      <>
        <path d="M5 18h14" {...stroke} />
        <path d="M7 14 10 11l3 2 4-5" {...stroke} />
      </>
    ),
    team: (
      <>
        <circle cx="9" cy="9" r="3" {...stroke} />
        <circle cx="16.5" cy="10.5" r="2.5" {...stroke} />
        <path d="M4.5 18a4.5 4.5 0 0 1 9 0" {...stroke} />
        <path d="M14 18a3 3 0 0 1 5.5-1.5" {...stroke} />
      </>
    ),
    partners: (
      <>
        <path d="M7.5 8.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" {...stroke} />
        <path d="M16.5 8.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" {...stroke} />
        <path d="M5 20v-1.2a3.8 3.8 0 0 1 3.8-3.8h2.2" {...stroke} />
        <path d="M19 20v-1.2a3.8 3.8 0 0 0-3.8-3.8H13" {...stroke} />
        <path d="M9.5 12.5 12 15l2.5-2.5" {...stroke} />
      </>
    ),
    workspace: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="3" {...stroke} />
        <path d="M8 9h8" {...stroke} />
        <path d="M8 13h5" {...stroke} />
      </>
    ),
    billing: (
      <>
        <rect x="4" y="7" width="16" height="10" rx="3" {...stroke} />
        <path d="M4 11h16" {...stroke} />
        <path d="M8 15h3" {...stroke} />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="acc-sidebar-link__glyph">
      {glyphs[name]}
    </svg>
  );
}

function isItemActive(
  item: SidebarItem,
  pathname: string,
  search: string,
): boolean {
  const params = new URLSearchParams(search);

  if (item.to) {
    if (item.to === '/dashboard') return pathname === '/dashboard';
    if (item.to === '/sites') {
      if (pathname !== '/sites') return false;
      if (item.view) return params.get('view') === item.view;
      return !params.get('view');
    }
    if (item.to === '/workspace') {
      if (pathname !== '/workspace') return false;
      if (item.section) return params.get('section') === item.section;
      return !params.get('section');
    }
    return pathname.startsWith(item.to);
  }

  return false;
}

function getItemPath(item: SidebarItem): string {
  if (!item.to) {
    return '#';
  }

  if (!item.view && !item.section) {
    return item.to;
  }

  const params = new URLSearchParams();
  if (item.view) {
    params.set('view', item.view);
  }
  if (item.section) {
    params.set('section', item.section);
  }

  return `${item.to}?${params.toString()}`;
}

function NavLinkContent({
  icon,
  label,
  collapsed,
  badge,
}: {
  icon: SidebarIconName;
  label: string;
  collapsed: boolean;
  badge?: string | number;
}) {
  return (
    <>
      <span className="acc-sidebar-link__icon" aria-hidden="true">
        <SidebarIcon name={icon} />
      </span>
      {!collapsed && (
        <>
          <span className="acc-sidebar-link__label">{label}</span>
          {badge !== undefined && badge !== null && badge !== '' ? (
            <span className="acc-sidebar-link__badge">{badge}</span>
          ) : null}
        </>
      )}
    </>
  );
}

export default function AppSidebar({
  collapsed,
  unreadAlerts,
  alertsEnabled,
}: AppSidebarProps) {
  const { t } = useTranslation(['navigation', 'partnerPortal']);
  const { partnerAccess } = useAuth();
  const location = useLocation();
  const isPartnerExperience = partnerAccess !== null && location.pathname.startsWith('/partner');
  const [openGroups, setOpenGroups] = useState<Record<NavGroupId, boolean>>({
    main: true,
    operations: true,
    governance: false,
    partner: true,
    admin: false,
  });
  const [showAlertsUpgradeModal, setShowAlertsUpgradeModal] = useState(false);
  const alertsBenefits = useMemo(
    () => [
      t('alertsUpgrade.benefits.one'),
      t('alertsUpgrade.benefits.two'),
      t('alertsUpgrade.benefits.three'),
    ],
    [t],
  );

  const groups = useMemo(() => {
    const makeItem = (item: SidebarItem) => item;

    if (isPartnerExperience) {
      return [
        {
          id: 'partner' as const,
          label: t('partnerPortal:shell.sidebarLabel'),
          items: [
            makeItem({
              id: 'partner-dashboard',
              label: t('items.partnerDashboard'),
              icon: 'dashboard',
              to: '/partner/dashboard',
            }),
            makeItem({
              id: 'partner-referrals',
              label: t('items.partnerReferrals'),
              icon: 'partners',
              to: '/partner/referrals',
            }),
            makeItem({
              id: 'partner-commissions',
              label: t('items.partnerCommissions'),
              icon: 'billing',
              to: '/partner/commissions',
            }),
            makeItem({
              id: 'partner-payouts',
              label: t('items.partnerPayouts'),
              icon: 'audit',
              to: '/partner/payouts',
            }),
            makeItem({
              id: 'partner-settings',
              label: t('items.partnerSettings'),
              icon: 'workspace',
              to: '/partner/settings',
            }),
          ],
        },
      ];
    }

    const resolvedGroups = [
      {
        id: 'main' as const,
        label: t('groups.main'),
        items: [
          makeItem({
            id: 'dashboard',
            label: t('dashboard'),
            icon: 'dashboard',
            to: '/dashboard',
          }),
          makeItem({
            id: 'sites',
            label: t('sites'),
            icon: 'sites',
            to: '/sites',
          }),
          makeItem({
            id: 'alerts',
            label: t('alerts'),
            icon: 'alerts',
            to: '/alerts',
            locked: alertsEnabled === false,
            badge:
              alertsEnabled === false
                ? t('planBadge')
                : unreadAlerts > 0
                  ? unreadAlerts > 99
                    ? '99+'
                    : unreadAlerts
                  : undefined,
          }),
          makeItem({
            id: 'reports',
            label: t('items.reports'),
            icon: 'reports',
            to: '/sites',
            view: 'reports',
          }),
        ],
      },
      {
        id: 'operations' as const,
        label: t('groups.operations'),
        items: [
          makeItem({
            id: 'queue',
            label: t('items.queue'),
            icon: 'queue',
            to: '/sites',
            view: 'queue',
          }),
          makeItem({
            id: 'issues',
            label: t('items.issues'),
            icon: 'issues',
            to: '/sites',
            view: 'issues',
          }),
          makeItem({
            id: 'evidence',
            label: t('items.evidence'),
            icon: 'evidence',
            to: '/sites',
            view: 'evidence',
          }),
          makeItem({
            id: 'auth',
            label: t('items.authProfiles'),
            icon: 'auth',
            to: '/sites',
            view: 'auth',
          }),
          makeItem({
            id: 'schedule',
            label: t('items.schedules'),
            icon: 'schedule',
            to: '/sites',
            view: 'schedules',
          }),
        ],
      },
      {
        id: 'governance' as const,
        label: t('groups.governance'),
        items: [
          makeItem({
            id: 'policies',
            label: t('items.policies'),
            icon: 'policies',
            to: '/workspace',
            section: 'governance',
          }),
          makeItem({
            id: 'audit',
            label: t('items.audit'),
            icon: 'audit',
            to: '/workspace',
            section: 'governance',
          }),
          makeItem({
            id: 'team-access',
            label: t('items.team'),
            icon: 'team',
            to: '/workspace',
            section: 'team',
          }),
        ],
      },
      {
        id: 'partner' as const,
        label: t('groups.partner'),
        items: partnerAccess ? [
          makeItem({
            id: 'partner-dashboard',
            label: t('items.partnerDashboard'),
            icon: 'partners',
            to: '/partner/dashboard',
          }),
          makeItem({
            id: 'partner-referrals',
            label: t('items.partnerReferrals'),
            icon: 'team',
            to: '/partner/referrals',
          }),
          makeItem({
            id: 'partner-commissions',
            label: t('items.partnerCommissions'),
            icon: 'billing',
            to: '/partner/commissions',
          }),
          makeItem({
            id: 'partner-payouts',
            label: t('items.partnerPayouts'),
            icon: 'audit',
            to: '/partner/payouts',
          }),
          makeItem({
            id: 'partner-settings',
            label: t('items.partnerSettings'),
            icon: 'workspace',
            to: '/partner/settings',
          }),
        ] : [],
      },
      {
        id: 'admin' as const,
        label: t('groups.admin'),
        items: [
          makeItem({
            id: 'workspace',
            label: t('workspace'),
            icon: 'workspace',
            to: '/workspace',
          }),
          makeItem({
            id: 'billing',
            label: t('billing'),
            icon: 'billing',
            to: '/billing',
          }),
        ],
      },
    ];

    return resolvedGroups.filter((group) => group.items.length > 0);
  }, [alertsEnabled, isPartnerExperience, partnerAccess, t, unreadAlerts]);

  useEffect(() => {
    const activeGroup = groups.find((group) =>
      group.items.some((item) =>
        isItemActive(item, location.pathname, location.search),
      ),
    )?.id;

    if (!activeGroup) {
      return;
    }

    setOpenGroups((current) =>
      current[activeGroup]
        ? current
        : {
            ...current,
            [activeGroup]: true,
          },
    );
  }, [groups, location.pathname, location.search]);

  const toggleGroup = (groupId: NavGroupId) => {
    setOpenGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  };

  return (
    <>
      <aside
        className={
          collapsed
            ? 'acc-shell__sidebar acc-shell__sidebar--collapsed'
            : 'acc-shell__sidebar'
        }
      >
        <div className="acc-sidebar__brand">
          <Link
            to={isPartnerExperience ? '/partner/dashboard' : '/dashboard'}
            aria-label={isPartnerExperience ? t('partnerPortal:shell.brandAriaLabel') : t('brandAriaLabel')}
            className="acc-sidebar__brand-link"
          >
            {collapsed ? (
              <BrandLogo variant="icon" tone="light" />
            ) : (
              <BrandLogo variant="auth" tone="light" />
            )}
          </Link>
          {!collapsed && (
            <div className="acc-sidebar__brand-meta">
              {isPartnerExperience ? t('partnerPortal:shell.brandMeta') : t('brandMeta')}
            </div>
          )}
        </div>

        <div className="acc-sidebar__groups">
          {groups.map((group) => (
            <section key={group.id} className="acc-sidebar-group">
              <button
                type="button"
                className="acc-sidebar-group__toggle"
                onClick={() => toggleGroup(group.id)}
                title={collapsed ? group.label : undefined}
              >
                {!collapsed && (
                  <>
                    <span className="acc-sidebar-group__label">{group.label}</span>
                    <span className="acc-sidebar-group__chevron" aria-hidden="true">
                      {openGroups[group.id] ? '⌄' : '›'}
                    </span>
                  </>
                )}
              </button>

              {(openGroups[group.id] || collapsed) && (
                <div className="acc-sidebar-group__items">
                  {group.items.map((item) => {
                    const active = isItemActive(
                      item,
                      location.pathname,
                      location.search,
                    );
                    const classes = [
                      'acc-sidebar-link',
                      active ? 'acc-sidebar-link--active' : '',
                      item.disabled ? 'acc-sidebar-link--disabled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    if (item.locked) {
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={classes}
                          onClick={() => setShowAlertsUpgradeModal(true)}
                          title={collapsed ? item.label : undefined}
                        >
                          <NavLinkContent
                            icon={item.icon}
                            label={item.label}
                            collapsed={collapsed}
                            badge={item.badge}
                          />
                        </button>
                      );
                    }

                    return (
                      <Link
                        key={item.id}
                        to={getItemPath(item)}
                        className={classes}
                        title={collapsed ? item.label : undefined}
                        aria-disabled={item.disabled ? 'true' : undefined}
                      >
                        <NavLinkContent
                          icon={item.icon}
                          label={item.label}
                          collapsed={collapsed}
                          badge={item.badge}
                        />
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
        </div>
      </aside>

      {showAlertsUpgradeModal && (
        <FeatureLockModal
          title={t('alertsUpgrade.title')}
          description={t('alertsUpgrade.description')}
          benefits={alertsBenefits}
          upgradeLabel={t('alertsUpgrade.upgradeLabel')}
          onClose={() => setShowAlertsUpgradeModal(false)}
          onUpgrade={() => {
            setShowAlertsUpgradeModal(false);
            window.location.href = ALERTS_UPGRADE_PATH;
          }}
          onPricing={() => {
            setShowAlertsUpgradeModal(false);
            window.location.href = '/pricing';
          }}
        />
      )}
    </>
  );
}
